import { ExtendedDataType } from '@shared/models';
import {
  DprStatusTrackerDetails,
  DprTableOfColumnDetail,
} from '@shared/models/dpr';
import {
  StatusTrackerColumn,
  StatusTrackerWithData,
  TableOfColumnItemWithValues,
  TableOfColumnWithData,
} from '@shared/models/project-setup';
import {
  isStatusTrackerColumnCarryOverEnabled,
  isTableOfColumnItemCarryOverEnabled,
} from './accumulation.utils';
import {
  getHistoryColumnIds,
  getStatusTrackerFormulas,
  getTableOfColumnFormulas,
} from './formula.utils';

/**
 * Carry-over and formula values are worked out every time a DPR is shown and are
 * never entered, so a save or a copy leaves them out: stored, they would pass for
 * values a user typed.
 */
export function isStatusTrackerColumnDerived(
  statusTracker: StatusTrackerWithData,
  column: StatusTrackerColumn
): boolean {
  return (
    column?.dataTypeId === ExtendedDataType.Formula ||
    isStatusTrackerColumnCarryOverEnabled(statusTracker, column)
  );
}

export function isTableOfColumnItemDerived(
  item: TableOfColumnItemWithValues
): boolean {
  return (
    item?.dataTypeId === ExtendedDataType.Formula ||
    isTableOfColumnItemCarryOverEnabled(item)
  );
}

/**
 * The derived columns of a status tracker that cannot be worked out without the
 * previous DPR: its carry-overs and the formulas that read the DPR before.
 */
export function getStatusTrackerHistoryColumnIds(
  statusTracker: StatusTrackerWithData
): Set<number> {
  return getHistoryColumnIds(
    getStatusTrackerFormulas(statusTracker),
    new Set(
      (statusTracker?.columns ?? [])
        .filter((column) =>
          isStatusTrackerColumnCarryOverEnabled(statusTracker, column)
        )
        .map((column) => +column.id)
    )
  );
}

export function getTableOfColumnsHistoryItemIds(
  tableOfColumn: TableOfColumnWithData
): Set<number> {
  return getHistoryColumnIds(
    getTableOfColumnFormulas(tableOfColumn),
    new Set(
      (tableOfColumn?.tableOfColumnItems ?? [])
        .filter(isTableOfColumnItemCarryOverEnabled)
        .map((item) => +item.id)
    )
  );
}

/**
 * The cells without the derived ones, whose stored value is stale. `storedColumnIds`
 * names the derived columns whose stored value is shown instead, for a DPR that came
 * without the previous values they are worked out from.
 */
export function withoutDerivedStatusTrackerCells(
  details: DprStatusTrackerDetails[],
  statusTrackers: StatusTrackerWithData[],
  storedColumnIds?: Map<number, Set<number>>
): DprStatusTrackerDetails[] {
  const derivedCells = new Set(
    (statusTrackers ?? []).flatMap((statusTracker) =>
      (statusTracker.columns ?? [])
        .filter(
          (column) =>
            isStatusTrackerColumnDerived(statusTracker, column) &&
            !storedColumnIds?.get(+statusTracker.id)?.has(+column.id)
        )
        .map((column) => `${+statusTracker.id}_${+column.id}`)
    )
  );

  return (details ?? []).filter(
    (detail) =>
      !derivedCells.has(`${+detail.statusTrackerId}_${+detail.columnId}`)
  );
}

/** As `withoutDerivedStatusTrackerCells`, for the rows of a table. */
export function withoutDerivedTableOfColumnItems(
  details: DprTableOfColumnDetail[],
  tableOfColumn: TableOfColumnWithData,
  storedItemIds?: Set<number>
): DprTableOfColumnDetail[] {
  const derivedItemIds = new Set(
    (tableOfColumn?.tableOfColumnItems ?? [])
      .filter(
        (item) =>
          isTableOfColumnItemDerived(item) && !storedItemIds?.has(+item.id)
      )
      .map((item) => +item.id)
  );

  return (details ?? []).map((detail) => ({
    ...detail,
    dprTableOfColumnDetailItems: detail.dprTableOfColumnDetailItems?.filter(
      (item) => !derivedItemIds.has(+item.tableOfColumnItemId)
    ),
  }));
}
